import { useState, useEffect } from "react";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import useMediaQuery from "@mui/material/useMediaQuery";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import logo from "assets/images/logos/LogoSemFundo.png";
import Breadcrumbs from "examples/Breadcrumbs";
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
  setOpenAccountMenu,
} from "context";
import { auth } from "firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import Badge from "@mui/material/Badge";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { firestore } from "firebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";
import MDButton from "components/MDButton";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode, openAccountMenu } = controller;
  const [openNotifications, setOpenNotifications] = useState(null);

  const route = useLocation().pathname.split("/").slice(1);
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");

  const [userName, setUserName] = useState("Usuário");

  useEffect(() => {
    setOpenAccountMenu(dispatch, false);
  }, [dispatch]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const firstName = user.displayName ? user.displayName.split(" ")[0] : "Usuário";
        setUserName(firstName);
      } else {
        navigate("/authentication/sign-in");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();

    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenAccountMenu = () => setOpenAccountMenu(dispatch, !openAccountMenu);
  const handleGoToDashboard = () => navigate("/dashboard");

  const iconsStyle = () => ({ color: "#1976d2" });
  const [notifications, setNotifications] = useState([]);
  const handleOpenNotifications = (event) => setOpenNotifications(event.currentTarget);
  const handleCloseNotifications = () => setOpenNotifications(null);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const userId = auth.currentUser?.uid;
        if (!userId) return;

        const readNotifications = JSON.parse(localStorage.getItem("readNotifications")) || [];
        const notificationsList = [];

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const lancamentosRef = collection(firestore, "usuarios", userId, "lancamentos");
        const snapshotPendentes = await getDocs(query(lancamentosRef, where("pago", "==", false)));

        snapshotPendentes.forEach((doc) => {
          const data = doc.data();
          const dueDate = new Date(data?.data?.split("/").reverse().join("-"));
          if (dueDate <= today && !readNotifications.includes(doc.id)) {
            const status = dueDate < today ? "vencida" : "vence hoje";
            notificationsList.push({
              id: doc.id,
              emoji: data.emoji || "❗",
              message: `${data.emoji || ""} Transação ${status}: ${data.categoria} - R$${data.valor}`,
              type: "transacao",
            });
          }
        });

        const categoriasRef = collection(firestore, "usuarios", userId, "categorias");
        const snapshotLimites = await getDocs(categoriasRef);

        snapshotLimites.forEach((doc) => {
          const data = doc.data();
          const percent = (data.consumo / data.limite) * 100;
          const notificationId = `limite-${doc.id}`;
          if (data.limite > 0 && percent >= 80 && !readNotifications.includes(notificationId)) {
            const status = percent >= 100 ? "atingiu" : "está acima de 80%";
            notificationsList.push({
              id: notificationId,
              emoji: data.emoji || "⚠️",
              message: `${data.emoji || ""} Limite de gastos ${status}: ${data.nome} - ${percent.toFixed(1)}%`,
              type: "limite",
            });
          }
        });

        const consultoriaRef = collection(firestore, "usuarios", userId, "consultor");
        const snapshotConsultoria = await getDocs(query(consultoriaRef, where("lido", "==", false)));

        snapshotConsultoria.forEach((doc) => {
          const data = doc.data();
          const notificationId = `consultoria-${doc.id}`;
          if (!readNotifications.includes(notificationId)) {
            notificationsList.push({
              id: notificationId,
              message: `Nova mensagem do consultor: ${data.conteudo}`,
              type: "consultoria",
            });
          }
        });

        setNotifications(notificationsList);
      } catch (error) {
        console.error("Erro ao buscar notificações:", error);
      }
    };

    fetchNotifications();
  }, []);

  const markAsRead = (id) => {
    const readNotifications = JSON.parse(localStorage.getItem("readNotifications")) || [];
    if (!readNotifications.includes(id)) {
      readNotifications.push(id);
      localStorage.setItem("readNotifications", JSON.stringify(readNotifications));
    }
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  return (
    <AppBar
      position={absolute ? "" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <MDTypography variant="h6" sx={{ color: "#1976d2", fontWeight: "medium", textTransform: "capitalize" }}>
            {route[route.length - 1]}
          </MDTypography>
        </MDBox>
        {isMini ? null : (
          <MDBox
            sx={(theme) => ({
              ...navbarRow(theme, { isMini }),
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "nowrap",
            })}
          >
            <MDTypography
              variant="body1"
              sx={{
                color: "#1976d2",
                marginRight: 2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Olá, {userName}
            </MDTypography>

            <MDBox color={light ? "white" : "inherit"} display="flex" alignItems="center" gap={0}>
              {!isMobile && (
                <>
                  <IconButton
                    size="small"
                    disableRipple
                    color="inherit"
                    sx={navbarIconButton}
                    onClick={handleGoToDashboard}
                  >
                    <Icon sx={iconsStyle}>home</Icon>
                  </IconButton>
                  <IconButton
                    size="small"
                    disableRipple
                    color="inherit"
                    sx={navbarIconButton}
                    onClick={handleOpenAccountMenu}
                  >
                    <Icon sx={iconsStyle}>account_circle</Icon>
                  </IconButton>
                  <IconButton
                    size="small"
                    disableRipple
                    color="inherit"
                    sx={navbarIconButton}
                    onClick={handleConfiguratorOpen}
                  >
                    <Icon sx={iconsStyle}>settings</Icon>
                  </IconButton>
                  <IconButton
                    size="small"
                    disableRipple
                    color="inherit"
                    sx={navbarMobileMenu}
                    onClick={handleMiniSidenav}
                  >
                    <Icon sx={iconsStyle} fontSize="medium">
                      {miniSidenav ? "menu_open" : "menu"}
                    </Icon>
                  </IconButton>
                </>
              )}
              {isMobile && (
                <MDBox display="flex" alignItems="center">
                  <IconButton
                    size="small"
                    disableRipple
                    color="inherit"
                    sx={navbarIconButton}
                    onClick={handleOpenAccountMenu}
                  >
                    <Icon sx={iconsStyle}>account_circle</Icon>
                  </IconButton>
                  <IconButton
                    size="small"
                    disableRipple
                    color="inherit"
                    sx={navbarMobileMenu}
                    onClick={handleMiniSidenav}
                  >
                    <Icon sx={iconsStyle} fontSize="medium">
                      {miniSidenav ? "menu_open" : "menu"}
                    </Icon>
                  </IconButton>
                </MDBox>
              )}
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleOpenNotifications}
              >
                <Badge badgeContent={notifications.length} color="error">
                  <NotificationsIcon sx={iconsStyle} />
                </Badge>
              </IconButton>
              <Menu
                anchorEl={openNotifications}
                open={Boolean(openNotifications)}
                onClose={handleCloseNotifications}
                PaperProps={{
                  sx: {
                    width: 320,
                    maxHeight: "80vh",
                    padding: 2,
                    overflowY: "auto",
                    zIndex: 1300,
                  },
                }}
              >
                {notifications.length > 0 ? (
                  notifications.map((notification, index) => (
                    <MDBox
                      key={index}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={1}
                      sx={{
                        padding: "8px",
                        borderRadius: "8px",
                        backgroundColor: "#f9f9f9",
                        "&:hover": { backgroundColor: "#ececec" },
                      }}
                    >
                      <MDBox display="flex" alignItems="center" gap={2}>
                        <span style={{ fontSize: "1.5rem" }}>{notification.message.split(" ")[0]}</span>
                        <MDTypography
                          variant="body2"
                          sx={{ fontSize: "0.875rem", wordBreak: "break-word", maxWidth: "200px" }}
                        >
                          {notification.message.slice(notification.message.indexOf(" ") + 1)}
                        </MDTypography>
                      </MDBox>
                      <MDButton size="small" variant="text" color="error" onClick={() => markAsRead(notification.id)}>
                        Lido
                      </MDButton>
                    </MDBox>
                  ))
                ) : (
                  <MDTypography variant="body2" textAlign="center" color="textSecondary" sx={{ marginTop: 2 }}>
                    Nenhuma notificação disponível
                  </MDTypography>
                )}
              </Menu>
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
